import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input, YStack, XStack, Text, Button, View } from "tamagui";
import { AxiosResponse } from "axios";
import liff from "@line/liff";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { RiArrowLeftSLine, RiPhoneLine, RiShieldCheckLine, RiCloseCircleLine } from "react-icons/ri";

import Header from "@components/layout/Header";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";
import RadiusCard from "@components/layout/RadiusCard";
import RadiusBoxLabel from "@components/layout/RadiusBoxLabel";
import { OTPInputBlocks } from "@components/form/OTPInputBlocks";
import { colors } from "@styles/colors";
import api, { handleRequest } from "@services/axios/apiManager";
import { useUtilsStore } from "@utils/store/utils/useUtilsTool";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { validateTaiwanPhone } from "@components/form/validate/vaildateTaiwanPhone";
import appToast from "@components/Toast";
import { getFamilyInviteCode, clearFamilyInviteCode } from "@utils/familyInvite";
import { getPayReturnTo, clearPayReturnTo } from "@utils/payReturnTo";
import { getSendOtpErrorMessage, getVerifyOtpErrorMessage } from "@utils/otpError";
import { VOICE_OTP_CALLER_ID } from "@utils/func/voiceOtp";
import { track } from "@lib/tracker";

import { maskPhone } from "@utils/func/maskPhone";

/** 從 axios error 取出後端錯誤訊息（後端多為中文，可直接顯示）。 */
const extractErrorMessage = (error: unknown): string => {
  if (error && typeof error === "object" && "response" in error) {
    const data = (error as { response?: { data?: { message?: unknown } } }).response?.data;
    if (data) {
      if (typeof data.message === "string") return data.message;
      if (Array.isArray(data.message)) return data.message.join("、");
    }
  }
  if (error instanceof Error) return error.message;
  return "";
};

/** 後端邀請碼無效原因 → 使用者友善訊息。 */
const mapInviteReason = (reason?: string | null): string => {
  const r = (reason || "").toLowerCase();
  if (r.includes("already been used")) return "此邀請碼已被使用";
  if (r.includes("not active")) return "此瓦斯帳戶目前無法加入";
  if (r.includes("issuer has left")) return "邀請碼已失效（邀請人已離開此帳戶）";
  if (r.includes("expired") || r.includes("invalid")) return "此邀請碼已過期或無效";
  return "此邀請碼無效";
};

/**
 * 零表單邀請註冊：未註冊者掃家庭邀請碼 QR 後落地此頁。
 * 只需手機 OTP 驗證 → 後端以 LINE 顯示名稱 + 驗證手機自動建立帳號並加入家庭（不需填資料）。
 */
const JoinByInvite = () => {
  const navigate = useNavigate();
  const { lineAccessToken } = useLineAuth();
  const { setCusJwtToken } = useCusAuth();
  const { setLoading } = useUtilsStore();
  // 進頁讀一次凍住：註冊成功會 clearFamilyInviteCode()，若每次 render 重讀，
  // 成功後 inviteCode 變 null 會再觸發「缺少邀請碼」守衛（誤報 + 導去 /home 與導回付款頁打架）。
  const [inviteCode] = useState(() => getFamilyInviteCode());

  const [sendState, setSendState] = useState<{ success?: boolean } | null>(null);
  const [countdown, setCountdown] = useState(0);
  // 語音倒數與簡訊分開：後端兩個管道各自計冷卻（簡訊 60s / 語音 90s）。
  // 共用一個倒數會在簡訊冷卻中把語音一起鎖住，而那正是收不到簡訊時最需要它的時刻。
  const [voiceCountdown, setVoiceCountdown] = useState(0);
  const [lastChannel, setLastChannel] = useState<"sms" | "voice">("sms");
  // 邀請碼預檢狀態：checking 檢查中 / valid 有效（顯示手機頁）/ invalid 無效或過期
  const [codeStatus, setCodeStatus] = useState<"checking" | "valid" | "invalid">("checking");
  const [invalidMsg, setInvalidMsg] = useState("此邀請碼已過期或無效");

  // 沒有邀請碼就不能用這個流程 → 導回首頁
  useEffect(() => {
    if (!inviteCode) {
      appToast.error("缺少邀請碼，請重新掃描邀請連結");
      const t = setTimeout(() => navigate("/home"), 1200);
      return () => clearTimeout(t);
    }
  }, [inviteCode, navigate]);

  // 進頁先驗證邀請碼是否仍有效（不消費）；過期/無效就不讓使用者白走手機驗證。
  useEffect(() => {
    if (!inviteCode || !lineAccessToken) return;
    let active = true;
    api
      .post("/app-api/line/invite-code-check", { accessToken: lineAccessToken, invite_code: inviteCode }, { isAuth: false })
      .then((res) => {
        if (!active) return;
        const { valid, reason } = (res?.data?.data ?? {}) as { valid?: boolean; reason?: string | null };
        if (valid) {
          setCodeStatus("valid");
        } else {
          setInvalidMsg(mapInviteReason(reason));
          setCodeStatus("invalid");
        }
      })
      .catch(() => {
        if (!active) return;
        setInvalidMsg("邀請碼驗證失敗，請稍後再試");
        setCodeStatus("invalid");
      });
    return () => {
      active = false;
    };
  }, [inviteCode, lineAccessToken]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown((p) => p - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  //@ 語音倒數（與簡訊各自獨立）
  useEffect(() => {
    if (voiceCountdown <= 0) return;
    const timer = setInterval(() => setVoiceCountdown((p) => p - 1), 1000);
    return () => clearInterval(timer);
  }, [voiceCountdown]);

  const formSchema = z.object({
    phone: z
      .string()
      .min(1, { message: "請輸入有效的手機號碼 (09開頭共10碼) 或市話 (含區碼)" })
      .refine(validateTaiwanPhone, { message: "請輸入有效的手機號碼 (09開頭共10碼) 或市話 (含區碼)" }),
    otp: z.string().optional(),
  });
  type FormData = z.infer<typeof formSchema>;

  const {
    control,
    handleSubmit,
    setError,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { phone: "", otp: "" },
  });

  // 發送驗證碼
  const onSendOtp = async (data: FormData, channel: "sms" | "voice" = "sms") => {
    if (!lineAccessToken) {
      liff.login();
      return;
    }
    track("funnel_step", { funnel: "join", step: channel === "voice" ? "otp_send_voice" : "otp_send" });
    handleRequest({
      asyncRequest: () =>
        api.post("/app-api/line/send-phone-otp", { accessToken: lineAccessToken, phone: data?.phone || "", channel }, { isAuth: false }),
      successCallback: (res: AxiosResponse) => {
        setSendState(res.data.data);
        // 冷卻秒數以後端為準，前端寫死會與後端對不起來（倒數歸零卻仍被 429 擋）。
        const secs = Number(res?.data?.data?.resendAvailableInSeconds) || (channel === "voice" ? 90 : 60);
        if (channel === "voice") setVoiceCountdown(secs);
        else setCountdown(secs);
        setLastChannel(channel);
        setValue("otp", "");
        appToast.success(channel === "voice" ? `電話將於 30 秒內撥入（${VOICE_OTP_CALLER_ID}）` : "驗證碼已發送");
      },
      errorCallback: (error: unknown) => {
        // 語音是在 OTP 畫面按的，此時手機欄位已不在畫面上 → 掛在欄位上使用者看不到。
        if (channel === "voice") appToast.error(getSendOtpErrorMessage(error));
        else setError("phone", { type: "manual", message: getSendOtpErrorMessage(error) });
      },
      setLoading,
    });
  };

  // 驗證 OTP → 成功後直接呼叫零表單邀請註冊
  const onVerifyOtp = async (otpValue: string) => {
    if (!otpValue || otpValue.length !== 5) {
      setError("otp", { type: "manual", message: "請輸入 5 位數驗證碼" });
      return;
    }
    if (!lineAccessToken || !inviteCode) return;
    const phone = getValues("phone");

    track("funnel_step", { funnel: "join", step: "otp_submit" });
    handleRequest({
      asyncRequest: () => api.post("/app-api/line/verify-phone-otp", { accessToken: lineAccessToken, phone, otpCode: otpValue }, { isAuth: false }),
      successCallback: (res: AxiosResponse) => {
        const phoneVerifiedTicket = res?.data?.data?.phoneVerifiedTicket || "";
        void registerByInvite(phoneVerifiedTicket);
      },
      errorCallback: (error: unknown) => setError("otp", { type: "manual", message: getVerifyOtpErrorMessage(error) }),
      setLoading,
    });
  };

  // 零表單註冊 + 加入家庭
  const registerByInvite = async (phoneVerifiedTicket: string) => {
    if (!lineAccessToken || !inviteCode || !phoneVerifiedTicket) return;
    track("funnel_step", { funnel: "join", step: "register_submit" });
    handleRequest({
      asyncRequest: () =>
        api.post(
          "/app-api/line/register-by-invite",
          { accessToken: lineAccessToken, phoneVerifiedTicket, invite_code: inviteCode },
          { isAuth: false },
        ),
      successCallback: (res: AxiosResponse) => {
        const data = res?.data?.data ?? {};
        if (data.jwtToken) {
          sessionStorage.setItem("jwtToken", data.jwtToken);
          setCusJwtToken(data.jwtToken);
        }
        clearFamilyInviteCode();
        appToast.success("已完成註冊並加入家庭");
        // 掃付款 QR 進來的：註冊加入後導回付款頁完成付款（full reload 載入新身分/jwt）。
        const payBack = getPayReturnTo();
        if (payBack) {
          clearPayReturnTo();
          window.location.href = payBack;
          return;
        }
        // 一般邀請連結：開啟家庭頁面（不再導去 OA）；有 context_id 就進該家庭詳情，否則回帳戶列表。
        if (data.context_id) {
          navigate(`/account/detail/${data.context_id}`, { replace: true });
        } else {
          navigate("/account", { replace: true });
        }
      },
      errorCallback: (error: unknown) => {
        // 顯示後端真正原因（例如「此電話已被其他帳號使用」「已經註冊過」「邀請碼已過期」）。
        const msg = extractErrorMessage(error);
        appToast.error(msg || "加入失敗，請確認後重試");
      },
      setLoading,
    });
  };

  return (
    <YStack height="100dvh" width="100vw" background={colors.bg}>
      <Header>
        <XStack ai="center" onPress={() => navigate(-1)} cursor="pointer" mb="$3">
          <View mr="$2">
            <RiArrowLeftSLine size={20} color={colors.light} />
          </View>
          <Text color={colors.light} fontWeight="bold" fontSize={16}>
            返回
          </Text>
        </XStack>
        <Text color={colors.light} fontSize={22} fontWeight="800" mb="$1.5">
          加入家庭
        </Text>
        <Text color="rgba(255,255,255,0.85)" fontSize={12}>
          驗證手機即完成加入，免填其他資料
        </Text>
      </Header>

      <RadiusContentWrapper>
        {codeStatus === "checking" ? (
          <YStack ai="center" jc="center" py="$10" gap="$3">
            <YStack
              w={22}
              h={22}
              br={11}
              borderWidth={3}
              borderColor={colors.primary}
              borderTopColor="transparent"
              style={{ animation: "spin 0.8s linear infinite" }}
            />
            <Text fontSize={14} color={colors.gray}>
              確認邀請碼中…
            </Text>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </YStack>
        ) : codeStatus === "invalid" ? (
          <YStack gap={15}>
            <RadiusCard>
              <YStack ai="center" gap="$3" py="$4">
                <View w={64} h={64} br={32} bg={colors.bgRed} ai="center" jc="center">
                  <RiCloseCircleLine size={34} color={colors.danger} />
                </View>
                <Text fontSize={18} fontWeight="800" color={colors.grayDark}>
                  無法加入
                </Text>
                <Text fontSize={14} color={colors.gray} ta="center" maw={280} lineHeight={20}>
                  {invalidMsg}
                </Text>
                <Text fontSize={12} color={colors.gray200} ta="center" maw={280}>
                  請聯絡邀請人在帳戶頁重新產生邀請碼，再掃描一次。
                </Text>
              </YStack>
            </RadiusCard>
            <XStack
              className="primary-bg-gradient"
              borderRadius={20}
              p={15}
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              onPress={() => navigate("/home")}
            >
              <Text color="white" fontSize={16} fontWeight="bold">
                返回首頁
              </Text>
            </XStack>
          </YStack>
        ) : !sendState?.success ? (
          <YStack gap={15}>
            <RadiusCard>
              <RadiusBoxLabel label="手機號碼（🇹🇼 +886）" icon={<RiPhoneLine color={colors.primary} />} />
              <YStack mt="$3">
                <Text fontSize={14} fontWeight="bold" color={colors.gray} mb={4}>
                  請輸入您的手機號碼
                </Text>
                <Controller
                  control={control}
                  name="phone"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Input
                      fontSize="$4"
                      placeholder="輸入手機號碼"
                      keyboardType="phone-pad"
                      textAlign="center"
                      letterSpacing={3}
                      value={value}
                      onBlur={onBlur}
                      borderColor={errors.phone ? "$red10" : undefined}
                      borderWidth={errors.phone ? 1 : 0}
                      onChangeText={(text: string) => onChange(text)}
                    />
                  )}
                />
                {errors.phone && (
                  <Text color="$red10" fontSize={14} mt="$2">
                    {errors.phone.message}
                  </Text>
                )}
              </YStack>
            </RadiusCard>
            <XStack
              className="primary-bg-gradient"
              borderRadius={20}
              p={15}
              mt={5}
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              onPress={handleSubmit((d) => onSendOtp(d))}
            >
              <Text color="white" fontSize={16} fontWeight="bold">
                取得驗證碼
              </Text>
            </XStack>
          </YStack>
        ) : (
          <YStack gap={15}>
            <RadiusCard>
              <RadiusBoxLabel label="驗證碼" icon={<RiShieldCheckLine color={colors.primary} />} />
              <YStack mt="$3" alignItems="center">
                <Text fontSize={14} fontWeight="bold" color={colors.gray} alignSelf="flex-start">
                  請輸入 5 位數驗證碼
                </Text>
                <Text fontSize={14} color={colors.gray} my="$3" fontWeight="bold">
                  {lastChannel === "voice" ? "已撥打電話至" : "已傳送至"} {maskPhone(getValues("phone"))}
                </Text>
                <Controller
                  control={control}
                  name="otp"
                  render={({ field: { onChange, value } }) => (
                    <OTPInputBlocks value={value || ""} onChange={onChange} onComplete={(val) => onVerifyOtp(val)} error={errors.otp?.message} />
                  )}
                />
                {errors.otp && (
                  <Text color="$red10" fontSize={14} mt="$2" alignSelf="center">
                    {errors.otp.message}
                  </Text>
                )}
                <YStack mt="$2" alignItems="center" gap="$1">
                  <Button
                    chromeless
                    size="$3"
                    color={countdown > 0 ? colors.gray : colors.primary}
                    disabled={countdown > 0}
                    onPress={() => onSendOtp(getValues())}
                  >
                    {countdown > 0 ? `重新傳送驗證碼 (${countdown}s)` : "重新傳送驗證碼"}
                  </Button>
                  {/* 收不到簡訊的救援管道：改由系統打電話唸驗證碼（市話也收得到）。
                      與「重新傳送」同為藍字，但不加粗、字級小一級，並以上方細線分開——
                      它是備援出口，不該和主要動作看起來像兩個同等選項。 */}
                  <XStack height={1} backgroundColor="#F1F5F9" width="55%" my="$2" />
                  <Button
                    chromeless
                    size="$2"
                    color={voiceCountdown > 0 ? "#CBD5E1" : colors.primary}
                    fontWeight="400"
                    disabled={voiceCountdown > 0}
                    onPress={() => onSendOtp(getValues(), "voice")}
                  >
                    {voiceCountdown > 0 ? `語音驗證 ${voiceCountdown} 秒後可再試` : "改用語音驗證"}
                  </Button>
                </YStack>
              </YStack>
            </RadiusCard>
            <Button
              chromeless
              size="$3"
              color={colors.primary}
              onPress={() => {
                setSendState(null);
                setValue("otp", "");
              }}
            >
              重新輸入手機號碼
            </Button>
          </YStack>
        )}
      </RadiusContentWrapper>
    </YStack>
  );
};

export default JoinByInvite;
